import { practicePricingTiers, startingPrice, lowestPlanPrice, highestPlanPrice } from "./pricing";
export const estatePlanningPage = {
  "title": "Estate Planning Lawyer SEO & Marketing | PageOne",
  "description": "Estate planning SEO company for estate, probate and elder law firms. Rank for wills, trusts and probate searches and book more consultations.",
  "intro": [
    "Estate planning lawyer SEO is the work of getting your firm found on Google, in the local map pack and in AI answers when someone searches for help with a will, a trust, a probate case or long-term care planning. Our SEO for estate planning lawyers focuses on the complex, local searches where your expertise matters: irrevocable trusts, Medicaid planning, special needs trusts and probate administration in your county. Those are searches DIY will sites can't win.",
    "Estate planning clients research for weeks before they book, so we pair SEO with estate planning marketing that keeps your firm in front of them: Google Ads, Local Services Ads, planning guides and email follow-up, all reported on one dashboard. Every page is reviewed against ABA Model Rule 7 and your state bar's advertising rules. If you want the full program across every practice area, see our [law firm SEO services](/services/law-firm-seo)."
  ],
  "byline": "Building search programs for law firms across personal injury, DUI, family and criminal defense.",
  "differences": [
    "**DIY tools own the broad terms.** LegalZoom, Trust & Will and Rocket Lawyer rank for \"online will\" and \"simple trust\". You win on complex, local searches they can't serve.",
    "**Clients decide slowly.** Most people compare firms for weeks before booking. Without guides and follow-up, they forget you.",
    "**An older audience.** Many clients are 55+ or adult children helping parents. Large type, simple navigation and fast pages matter more here than almost anywhere.",
    "**Trust is everything.** Wills, trusts and probate are \"Your Money or Your Life\" topics for Google, so pages need clear authorship, attorney review and accurate information. See [YMYL](/glossary/ymyl)."
  ],
  "searches": [
    "estate planning attorney near me",
    "estate planning lawyer [your city]",
    "trust attorney [your city]",
    "probate lawyer [your county]",
    "elder law attorney near me",
    "Medicaid planning lawyer [your city]",
    "estate planning attorney free consultation",
    "best estate planning attorneys in [your city]"
  ],
  "includedIntro": "Our estate planning SEO services cover everything an estate, probate or elder law firm needs to rank and convert. Each part is done by our team, reviewed by your attorneys for accuracy, and reported monthly.",
  "cards": [
    {
      "title": "Practice pages for every planning need",
      "body": "Dedicated pages for wills, revocable trusts, irrevocable trusts, special needs trusts, Medicaid and long-term care planning, powers of attorney, estate tax planning and estate litigation. Each page explains the service, who it's for, how the process works and what it costs to get started."
    },
    {
      "title": "Probate administration hub",
      "body": "A probate hub with pages for each county probate court you appear in, so you rank where families are actually dealing with an estate."
    },
    {
      "title": "Elder law SEO",
      "body": "Elder law pages for Medicaid planning, guardianship and long-term care, written for adult children as well as older clients."
    },
    {
      "title": "Google Business Profile and local SEO",
      "body": "Google Business Profile optimization, categories, services, posts, citations and a steady review program to win the map pack. See our [local SEO for law firms](/services/local-seo-for-law-firms)."
    },
    {
      "title": "Senior-friendly website experience",
      "body": "Large type, simple navigation, strong color contrast and fast mobile pages, so older visitors can read, trust and contact you."
    },
    {
      "title": "Lead magnets and email nurture",
      "body": "A will checklist, a probate timeline and planning webinars that capture interest, followed by email content that keeps your firm top of mind until they book."
    },
    {
      "title": "Structured data",
      "body": "LegalService, Attorney, Service, BreadcrumbList and FAQPage markup so Google understands your firm, your attorneys and your services."
    },
    {
      "title": "Editorial and legal-directory links",
      "body": "Links from local organizations, legal directories and editorial placements, with no link schemes. See our [link building for law firms](/services/link-building-for-law-firms)."
    },
    {
      "title": "AI search visibility",
      "body": "Content structured so your firm can be named in Google AI Overviews, ChatGPT and Perplexity answers about wills, trusts and probate in your area. See [AI SEO & GEO](/services/ai-seo-geo)."
    }
  ],
  "charges": [
    [
      "Wills",
      "\"will lawyer near me\", \"how much does a will cost [state]\"",
      "Wills page"
    ],
    [
      "Revocable trusts",
      "\"revocable trust attorney [city]\", \"living trust vs will\"",
      "Revocable trusts page"
    ],
    [
      "Irrevocable trusts & Medicaid",
      "\"Medicaid planning lawyer\", \"irrevocable trust attorney [city]\"",
      "Irrevocable trusts & Medicaid planning page"
    ],
    [
      "Special needs trusts",
      "\"special needs trust attorney\"",
      "Special needs trusts page"
    ],
    [
      "Probate",
      "\"probate lawyer [county]\", \"how long does probate take in [state]\"",
      "Probate hub + county probate court pages"
    ],
    [
      "Elder law",
      "\"elder law attorney near me\", \"guardianship lawyer [city]\"",
      "Elder law page"
    ],
    [
      "Estate tax",
      "\"estate tax planning attorney\"",
      "Estate tax planning page (see [tax lawyer SEO](/practice-areas/tax-law-lawyer-seo))"
    ],
    [
      "Estate litigation",
      "\"contested will lawyer\", \"trust dispute attorney\"",
      "Estate litigation page"
    ]
  ],
  "marketingIntro": "SEO compounds over months. Because estate planning clients decide slowly, our estate planning attorney marketing also works on the weeks between their first search and their consultation.",
  "marketing": [
    {
      "title": "Google Ads for estate planning attorneys",
      "body": "Search campaigns for wills, trusts and probate searches, with a landing page for each service. Clicks in this practice area are relatively affordable, so ads can work well alongside SEO from day one. Managed by our [PPC team for law firms](/services/ppc-for-law-firms)."
    },
    {
      "title": "Google Local Services Ads",
      "body": "The \"Google Screened\" ads at the very top of results. You pay per lead, not per click. We handle setup, verification, reviews and lead disputes."
    },
    {
      "title": "Estate planning advertising that's bar-compliant",
      "body": "Every ad, landing page and disclaimer is reviewed against your state's attorney advertising rules before it goes live. See [attorney advertising disclaimers](/glossary/attorney-advertising-disclaimer)."
    },
    {
      "title": "Email nurture and webinars",
      "body": "Planning guides, a will checklist, a probate timeline and webinar invitations that turn first-time visitors into booked consultations."
    }
  ],
  "comparison": [
    {
      "k": "Cost per click",
      "seo": "$0. You don't pay per visit.",
      "ads": "About $5–$8 average CPC for main US estate planning searches and $8–$10 for probate searches*"
    },
    {
      "k": "Time to first results",
      "seo": "60–120 days",
      "ads": "Within days"
    },
    {
      "k": "When you stop paying",
      "seo": "Rankings don't vanish overnight (they need upkeep)",
      "ads": "Leads stop when spend stops"
    },
    {
      "k": "How searchers see it",
      "seo": "Organic results and the map pack",
      "ads": "Labeled \"Sponsored\""
    },
    {
      "k": "Who owns it",
      "seo": "Your firm owns the pages and rankings",
      "ads": "You rent the placement"
    }
  ],
  "steps": [
    {
      "n": "01",
      "t": "Audit (10 business days).",
      "d": "A technical, on-page, Google Business Profile, backlink and competitor audit of your estate planning market."
    },
    {
      "n": "02",
      "t": "Roadmap.",
      "d": "A 12-month plan: a keyword map for every planning need, a content calendar, link targets and monthly KPIs tied to signed clients."
    },
    {
      "n": "03",
      "t": "Build.",
      "d": "Practice pages, the probate hub, schema and speed fixes, with attorney-reviewed content shipped every week."
    },
    {
      "n": "04",
      "t": "Grow.",
      "d": "Ongoing content, editorial links, Google Business Profile posts, reviews and email nurture, plus ads where they pay back."
    },
    {
      "n": "05",
      "t": "Report.",
      "d": "Monthly plain-English reporting on rankings, calls, forms, consultations and signed clients, with a quarterly strategy review."
    }
  ],
  "faqs": [
    {
      "q": "How do you market an estate planning law firm?",
      "a": "Start where clients search: practice pages for each planning need, a probate hub for your counties and a fully optimized Google Business Profile. Then add what keeps them engaged while they decide: planning guides, a will checklist, email follow-up and, if budget allows, Google Ads and Local Services Ads. Measure everything by consultations and signed clients."
    },
    {
      "q": "What is the best online marketing for estate planning attorneys?",
      "a": "For most firms it's a mix: SEO for wills, trusts and probate searches in your area, Google Business Profile for map pack visibility, and Google Ads or Local Services Ads for steady consultations while SEO builds. Because estate planning clients research for weeks, email follow-up often decides who they book."
    },
    {
      "q": "What is the best SEO company for estate planning attorneys?",
      "a": "Look for a company that works with estate planning firms specifically, understands probate and elder law, has attorneys review content for accuracy, follows bar advertising rules and reports on signed clients rather than clicks. Ask to see results from a comparable market and check that they won't work with your direct competitors."
    },
    {
      "q": "What is elder law SEO?",
      "a": "Elder law SEO gets your firm found for searches about Medicaid planning, guardianship and long-term care, often made by adult children on behalf of a parent. It needs its own pages, written in plain language, with clear next steps for families under time pressure."
    },
    {
      "q": "How do estate planning attorneys get more leads?",
      "a": "Rank for the specific services people search for in your area, make your Google Business Profile complete and well reviewed, offer something useful in exchange for an email (a checklist or probate timeline), and follow up. Paid ads and Local Services Ads add leads quickly while SEO builds."
    },
    {
      "q": "Is SEO or Google Ads better for estate planning firms?",
      "a": "Both work, and in estate planning ads are relatively affordable per click. Ads bring consultations quickly; SEO wins the complex, high-value searches like trusts, Medicaid planning and probate over time. Most firms do best running both."
    },
    {
      "q": "How do we compete with LegalZoom on broad terms?",
      "a": "We don't. We target the underserved local + complexity keywords ('irrevocable trust attorney [city]', 'probate lawyer [county]') where LegalZoom can't compete on expertise."
    },
    {
      "q": "How long until an estate planning firm sees ranking movement?",
      "a": "Most estate planning firms see local pack and long-tail movement in 60–120 days, with primary metro keywords ranking between months 6–12 depending on competition."
    },
    {
      "q": "What contract length do you require?",
      "a": "Engagements are 12 months because SEO compounds over that horizon. There is no auto-renewal, no cancellation penalty at renewal, and month-to-month conversion is available after month 12."
    },
    {
      "q": "Who owns the content, links, and site changes?",
      "a": "You do, from day one. All content, backlinks, tracking, and site changes are the client's property. If we part ways, everything stays with your firm."
    },
    {
      "q": "How does reporting work?",
      "a": "Monthly reports include rankings, organic traffic, tracked calls, form submissions, consultations booked, and signed cases, with a plain-English summary a partner can read in five minutes. Quarterly strategy reviews are led by your senior strategist."
    },
    {
      "q": "What happens if rankings drop after a Google update?",
      "a": "We monitor every core update and roll out a recovery plan within 10 business days, at no extra cost."
    },
    {
      "q": "Do you work with more than one estate planning firm in the same city?",
      "a": "No. We work with one estate planning firm per metro market. If we take your estate planning practice in a city, we won't take a competitor's, that exclusivity is written into every engagement."
    },
    {
      "q": "What's included versus billed as an add-on?",
      "a": "Strategy, technical SEO, on-page, content, GBP, link acquisition, reviews, and reporting are all included in every retainer. Video production, paid media management, and full site rebuilds are quoted separately."
    },
    {
      "q": "Are you bar-compliant?",
      "a": "Yes. Every published asset passes an ABA Model Rule 7 + state bar advertising review, and we keep a compliance log tied to every page and ad."
    }
  ],
  "pricing": practicePricingTiers,
  "serviceSchema": {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Estate Planning Lawyer SEO & Marketing",
    "serviceType": "Search engine optimization and digital marketing for estate planning, probate and elder law firms",
    "description": "Estate planning SEO company for estate, probate and elder law firms. Rank for wills, trusts and probate searches and book more consultations.",
    "url": "https://pageonelawyers.com/practice-areas/estate-planning-lawyer-seo",
    "provider": {
      "@type": "Organization",
      "name": "PageOne Lawyers",
      "url": "https://pageonelawyers.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Estate planning, probate and elder law firms and attorneys"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": lowestPlanPrice,
      "highPrice": highestPlanPrice,
      "offerCount": "6",
      "url": "https://pageonelawyers.com/pricing"
    }
  },
  "breadcrumbSchema": {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pageonelawyers.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Practice Areas",
        "item": "https://pageonelawyers.com/practice-areas"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Estate Planning Lawyer SEO",
        "item": "https://pageonelawyers.com/practice-areas/estate-planning-lawyer-seo"
      }
    ]
  },
  "related": [
    {
      "anchor": "Boston estate planning case study",
      "target": "/case-studies/estate-planning-boston"
    },
    {
      "anchor": "Tax lawyer SEO",
      "target": "/practice-areas/tax-law-lawyer-seo"
    },
    {
      "anchor": "SEO for solo lawyers and small law firms",
      "target": "/blog/solo-lawyer-small-firm-seo"
    },
    {
      "anchor": "Keyword research for law firms",
      "target": "/blog/keyword-research-for-law-firms"
    },
    {
      "anchor": "How law firms turn SEO traffic into new clients",
      "target": "/blog/how-law-firms-turn-seo-traffic-into-new-clients"
    },
    {
      "anchor": "What is a Google Business Profile?",
      "target": "/glossary/google-business-profile"
    }
  ],
  "caseChallenge": "A traditional Boston estate planning firm with an outdated site couldn't compete with LegalZoom, Trust & Will and Rocket Lawyer on broad terms like \"online will\" or \"simple trust\". Its real expertise, irrevocable trusts, Medicaid asset protection and complex probate, was buried under generic content aimed at the wrong audience.",
  "caseApproach": [
    "Moved the content strategy away from DIY-competitive terms toward complex local searches",
    "Published in-depth pages on irrevocable trusts, Medicaid planning, special needs trusts and estate tax planning",
    "Built a probate administration hub tied to the Suffolk, Middlesex and Norfolk county probate courts",
    "Launched a monthly attorney-authored newsletter",
    "Added Attorney, LegalService and FAQ schema to every service page"
  ],
  "caseResults": "Estate planning consultations grew 184% in 9 months. Trust formation inquiries, the firm's highest-value engagement, grew 3.1x. Average time on page rose 62% above the industry benchmark, and the firm ranks #1 for \"Medicaid asset protection attorney Boston\".",
  "pricingIntro": `Plans start at ${startingPrice}/month, with no setup fees and no surprise add-ons. Every plan includes attorney-reviewed content and full transparency on every deliverable. The right plan depends on how many practice pages, locations and probate courts you need to cover.`,
  "why": [
    "**One firm per market.** We don't work with competing estate planning firms in the same city.",
    "**Bar-compliant by default.** Every page and ad is reviewed against ABA Model Rule 7 and your state bar's advertising rules.",
    "**Attorney-reviewed content.** Your attorneys approve every page for legal accuracy before it goes live.",
    "**Measured in signed clients.** We report calls, consultations and signed clients, not just rankings.",
    "**You own everything.** Content, links and site changes stay with your firm if you leave."
  ],
  "elderIntro": [
    "Elder law and probate are where estate planning firms earn their highest-value work, and they need their own pages. Our elder law SEO builds pages for Medicaid planning, guardianship and long-term care, written for the adult children who often make the first search. For probate, we build a probate administration hub with pages for each county court you practice in, so \"probate lawyer [county]\" searches land on a page about that exact court.",
    "Both are covered in every estate planning program. You don't need a separate engagement."
  ]
};
